import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import { setLogDeleteFrequency, getLogDeleteFrequency } from "../../../services/logsService";
import { setAlertLogDeleteFrequency, getAlertLogDeleteFrequency } from "../../../services/EmailLogsService";
import { appSettingsId } from "../../../utils/constants";
import './LogManagement.css'

const LogManagement: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState<string>("log");
  const [frequency, setFrequency] = useState(1);
  const [day, setDay] = useState("day");
  const [alertFrequency, setAlertFrequency] = useState(1);
  const [alertDay, setAlertDay] = useState("day");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [emailToDelete, setEmailToDelete] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const fetchFrequency = async () => {

      setIsLoading(true);
      try {
        const logResponse = await getLogDeleteFrequency(appSettingsId.LOG_DELETE_FREQUENCY);
        const alertResponse = await getAlertLogDeleteFrequency(appSettingsId.ALERT_LOG_DELETE_FREQUENCY);

        if (logResponse && logResponse.Value) {
          const logValue = parseInt(logResponse.Value, 10);
          setFrequency(logValue);
          setDay(logValue > 1 ? "days" : "day");
        }

        if (alertResponse && alertResponse.Value) {
          const alertValue = parseInt(alertResponse.Value, 10);
          setAlertFrequency(alertValue);
          setAlertDay(alertValue > 1 ? "days" : "day");
        }
      } catch (error) {
        setIsError(true);
        console.error("Error fetching frequencies:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFrequency();
  }, [selectedTab]);

  const handleSave = async () => {
    try {
      const response = await setLogDeleteFrequency(appSettingsId.LOG_DELETE_FREQUENCY, frequency);
      if (response.status === 204) {
        toast.success("Log delete frequency updated successfully.");
      } else {
        toast.error(`${response.status} Failed to update log delete frequency.`);
      }
    } catch (error) {
      console.error("Error setting log delete frequency:", error);
      toast.error(`${error}`);
    }
  };

  const handleAlertSave = async () => {
    try {
      const response = await setAlertLogDeleteFrequency(appSettingsId.ALERT_LOG_DELETE_FREQUENCY, alertFrequency);
      if (response.status === 204) {
        toast.success("Alert log delete frequency updated successfully.");
      } else {
        toast.error(`${response.status} Failed to update alert log delete frequency.`);
      }
    } catch (error) {
      console.error("Error setting alert log delete frequency:", error);
      toast.error(`${error}`);
    }
  };

  const onDelete = (email: string) => {
    setEmailToDelete(email);
    setDialogOpen(true);
    setDialogMessage(`Are you sure you want to delete ${email}?`);
  };

  const onConfirmDelete = () => {
    setDialogOpen(false);
    // Assuming there's a deleteEmail function that deletes an email
    console.log("Email deleted: ", emailToDelete);
    setEmailToDelete(undefined);
  };

  return (
    <>
      <div className="page-title">
        <p>Log Management</p>
        <p className="page-subtitle">Select the lifespan for the logs.</p>
      </div>
      <div className="container-fluid mt-2">
        <ul className="nav nav-tabs" id="myTab" role="tablist">
          <li className="nav-item" role="presentation" onClick={() => setSelectedTab("log")}>
            <a
              className={selectedTab === "log" ? "nav-link active" : "nav-link"}
              id="log-tab"
              data-bs-toggle="tab"
              role="tab"
              aria-controls="log"
              aria-selected={selectedTab === "log" ? "true" : "false"}
            >
              Log Lifespan
            </a>
          </li>
          <li className="nav-item" role="presentation" onClick={() => setSelectedTab("alert")}>
            <a
              className={selectedTab === "alert" ? "nav-link active" : "nav-link"}
              id="alert-tab"
              data-bs-toggle="tab"
              role="tab"
              aria-controls="alert"
              aria-selected={selectedTab === "alert" ? "true" : "false"}
            >
              Email Log Lifespan
            </a>
          </li>
        </ul>
      </div>
      {selectedTab === "log" ? (
        <>
          <div className="d-flex justify-content-evenly align-items-center log-manage-container w-100 px-5">
              <p className="mb-0 fs-6 ms-3 flex-grow-1">Delete logs older than</p>
              <input
                className="form-control form-control-sm log-manage-input text-center"
                style={{ width: "60px" }}
                type="number"
                min="1"
                value={frequency}
                onChange={(e) => {
                  const value = Math.max(0, Number(e.target.value));
                  setDay(value > 1 ? "days" : "day");
                  setFrequency(value);
                }}
              />
              <p className="mb-0 fs-6 flex-grow-1">{day}</p>
              <button className="btn btn-primary ms-4" onClick={handleSave} disabled={frequency === 0}>
                Save
              </button>
          </div>

        </>
      ) : (
        <>
          <div className="d-flex justify-content-evenly align-items-center log-manage-container px-5">
            <p className="mb-0 ms-3 fs-6 flex-grow-1">Delete email logs older than</p>
            <input
              className="form-control form-control-sm log-manage-input text-center"
              style={{ width: "60px" }}
              type="number"
              min="1"
              value={alertFrequency}
              onChange={(e) => {
                const value = Math.max(0, Number(e.target.value));
                setAlertDay(value > 1 ? "days" : "day");
                setAlertFrequency(value);
              }}
            />
            <p className="mb-0 fs-6 flex-grow-1">{alertDay}</p>
            <button className="btn btn-primary ms-4" onClick={handleAlertSave} disabled={alertFrequency === 0}>
              Save
            </button>
          </div>
        </>
      )}

      <ToastContainer position="top-right" autoClose={5000} theme="light" />
    </>
  );
};

export default LogManagement;
